/**
 * The YubiKey pool's state — the same optimistic-apply-then-reconcile shape `content/hooks.ts`'s
 * `useManagedContentList` gives content, over the fleet-wide pool instead of a locationId-scoped
 * list.
 */
import type { CreateYubikey, DeleteResult, UpdateYubikey, Yubikey } from '@apuestapr/contracts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ApiClient, ApiFailure, ApiResult } from '../api/client.ts';
import { accessFor, type MutationState, type RequestState } from '../api/request-state.ts';
import { createYubikeysApi } from './api.ts';

export interface ManagedYubikeys {
  readonly state: RequestState<readonly Yubikey[]>;
  readonly mutation: MutationState<Yubikey | DeleteResult>;
  readonly reconciling: boolean;
  readonly create: (input: CreateYubikey) => Promise<ApiResult<Yubikey>>;
  readonly update: (id: number, input: UpdateYubikey) => Promise<ApiResult<Yubikey>>;
  readonly remove: (id: number) => Promise<ApiResult<DeleteResult>>;
  readonly reconcile: () => Promise<void>;
}

export const useManagedYubikeys = (
  client: ApiClient,
  csrfToken: string,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): ManagedYubikeys => {
  const api = useMemo(() => createYubikeysApi(client), [client]);
  const [state, setState] = useState<RequestState<readonly Yubikey[]>>({ status: 'loading' });
  const [mutation, setMutation] = useState<MutationState<Yubikey | DeleteResult>>({
    status: 'ready',
  });
  const [reconciling, setReconciling] = useState(false);
  const mounted = useRef(true);
  const generation = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reconcile = useCallback(async (): Promise<void> => {
    const current = generation.current + 1;
    generation.current = current;
    setReconciling(true);
    const result = await api.list();
    if (!mounted.current || current !== generation.current) return;
    setReconciling(false);
    if (result.ok) setState({ status: 'ready', data: result.data });
    else onFailure?.(result.failure);
  }, [api, onFailure]);

  useEffect(() => {
    let active = true;
    void api.list().then((result) => {
      if (!active) return;
      if (result.ok) setState({ status: 'ready', data: result.data });
      else {
        onFailure?.(result.failure);
        setState({ status: 'error', failure: result.failure, access: accessFor(result.failure) });
      }
    });
    return () => {
      active = false;
    };
  }, [api, onFailure]);

  const runMutation = useCallback(
    async <Value>(
      operation: () => Promise<ApiResult<Value>>,
      apply: (rows: readonly Yubikey[], value: Value) => readonly Yubikey[],
    ): Promise<ApiResult<Value>> => {
      setMutation({ status: 'loading' });
      const result = await operation();
      if (!mounted.current) return result;
      if (!result.ok) {
        onFailure?.(result.failure);
        setMutation({
          status: 'error',
          failure: result.failure,
          access: accessFor(result.failure),
        });
        return result;
      }
      setState((current) =>
        current.status === 'ready'
          ? { status: 'ready', data: apply(current.data, result.data) }
          : current,
      );
      setMutation({ status: 'ready', data: result.data as Yubikey | DeleteResult });
      void reconcile();
      return result;
    },
    [onFailure, reconcile],
  );

  const create = useCallback(
    (input: CreateYubikey) =>
      runMutation(
        () => api.create(input, csrfToken),
        (rows, row) => [...rows, row],
      ),
    [api, csrfToken, runMutation],
  );
  const update = useCallback(
    (id: number, input: UpdateYubikey) =>
      runMutation(
        () => api.update(id, input, csrfToken),
        (rows, row) => rows.map((current) => (current.id === row.id ? row : current)),
      ),
    [api, csrfToken, runMutation],
  );
  const remove = useCallback(
    (id: number) =>
      runMutation(
        () => api.remove(id, csrfToken),
        (rows) => rows.filter((row) => row.id !== id),
      ),
    [api, csrfToken, runMutation],
  );

  return { state, mutation, reconciling, create, update, remove, reconcile };
};
